import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { adminLogin } from '../services/api';

const AdminLogin = ({ onLogin, compact = false }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await adminLogin(email, password);
      if (res.data.access_token) {
        onLogin(res.data.access_token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  // Compact mode - for tooltip style
  if (compact) {
    return (
      <form onSubmit={handleSubmit} className="p-4 space-y-3">
        <div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm text-white/80 placeholder-white/30 focus:border-white/20 focus:outline-none transition font-['Jost']"
            placeholder="Email"
            required
            data-testid="login-email"
          />
        </div>

        <div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm text-white/80 placeholder-white/30 focus:border-white/20 focus:outline-none transition font-['Jost']"
            placeholder="Senha"
            required
            data-testid="login-password"
          />
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-2 bg-red-500/10 border border-red-500/20 rounded-lg text-[11px] text-red-400/80 font-['Jost']"
            data-testid="login-error"
          >
            {error}
          </motion.div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-2.5 bg-white/[0.08] hover:bg-white/[0.12] border border-white/[0.1] rounded-xl text-sm font-['Jost'] font-medium text-white/70 transition disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="login-submit"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Entrando...
            </span>
          ) : 'Entrar'}
        </button>
      </form>
    );
  }

  // Full mode - original layout
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-[#0a0a0c] flex items-center justify-center p-6"
    >
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-white/[0.08] to-white/[0.02] rounded-2xl border border-white/[0.06] flex items-center justify-center">
            <span className="text-3xl font-['Playfair_Display'] font-bold text-white/80">W</span>
          </div>
          <h1 className="text-xl font-['Jost'] font-medium text-white/80 tracking-wide">Admin Login</h1>
          <p className="text-xs font-['Jost'] text-white/30 mt-1">WClub Synergy</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[11px] font-['Jost'] text-white/40 uppercase tracking-wider mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-sm text-white/80 placeholder-white/20 focus:border-white/20 focus:outline-none transition font-['Jost']"
              placeholder="admin@wclub.com"
              required
              data-testid="login-email"
            />
          </div>

          <div>
            <label className="block text-[11px] font-['Jost'] text-white/40 uppercase tracking-wider mb-2">Senha</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-sm text-white/80 placeholder-white/20 focus:border-white/20 focus:outline-none transition font-['Jost']"
              placeholder="********"
              required
              data-testid="login-password"
            />
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-xs text-red-400/80 font-['Jost']"
              data-testid="login-error"
            >
              {error}
            </motion.div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-white/[0.08] hover:bg-white/[0.12] border border-white/[0.1] rounded-xl text-sm font-['Jost'] font-medium text-white/70 transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="login-submit"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Entrando...
              </span>
            ) : 'Entrar'}
          </button>
        </form>

        {/* Hint */}
        <p className="text-center text-[10px] font-['Jost'] text-white/20 mt-8">
          Acesso restrito a administradores
        </p>
      </div>
    </motion.div>
  );
};

export default AdminLogin;
